/**
 * 顶栏的身份区：当前账号 + 改口令 + 退出。
 *
 * 未登录（本地开发模式或用 API Key 调试）时整块不渲染 —— 显示一个点不动的
 * 头像、或一个退不了的「退出登录」，比什么都不显示更让人困惑。
 */

import { useState } from 'react';
import { App as AntdApp, Avatar, Button, Dropdown, Form, Input, Modal, Tag } from 'antd';
import { KeyOutlined, LogoutOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '@/api/auth';
import { isApiRequestError } from '@/api/client';
import { useAuthStore } from '@/store/auth';

const ROLE_LABEL: Record<string, string> = {
  ADMIN: '管理员',
  EDITOR: '编辑',
  VIEWER: '只读',
};

interface PasswordForm {
  old_password: string;
  new_password: string;
  confirm: string;
}

export function UserMenu() {
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);

  const [pwOpen, setPwOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<PasswordForm>();

  if (!user) return null;

  const onSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const onChangePassword = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await changePassword(values.old_password, values.new_password);
      setPwOpen(false);
      form.resetFields();
      // 服务端保留了当前会话、踢掉其余设备，所以这里**不需要**重新登录。
      // 提示里说清楚，否则用户会以为改完还得再登一次。
      message.success('口令已更新，其它设备已下线');
    } catch (e) {
      message.error(isApiRequestError(e) ? e.message : '修改失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dropdown
        placement="bottomRight"
        menu={{
          items: [
            {
              key: 'who',
              disabled: true,
              label: (
                <div className="aigzl-user__card">
                  <div className="aigzl-user__name">{user.display_name || user.username}</div>
                  <Tag bordered={false}>{ROLE_LABEL[user.role] ?? user.role}</Tag>
                </div>
              ),
            },
            { type: 'divider' },
            {
              key: 'password',
              icon: <KeyOutlined />,
              label: '修改口令',
              onClick: () => setPwOpen(true),
            },
            ...(user.role === 'ADMIN'
              ? [
                  {
                    key: 'users',
                    icon: <TeamOutlined />,
                    label: '账号管理',
                    onClick: () => navigate('/settings/users'),
                  },
                ]
              : []),
            { type: 'divider' as const },
            {
              key: 'logout',
              icon: <LogoutOutlined />,
              label: '退出登录',
              danger: true,
              onClick: () => void onSignOut(),
            },
          ],
        }}
      >
        <Button type="text" size="small" aria-label="账号菜单" className="aigzl-user__trigger">
          <Avatar size={22} icon={<UserOutlined />} />
          <span className="aigzl-user__label">{user.display_name || user.username}</span>
          {/* 只读角色常驻显示。写操作会被后端 403 挡下，但让人点完才知道太糟——
              页面上的按钮不逐个禁用（面太大），至少让"我是只读"一眼可见。 */}
          {user.role === 'VIEWER' ? (
            <Tag bordered={false} className="aigzl-user__ro">
              只读
            </Tag>
          ) : null}
        </Button>
      </Dropdown>

      <Modal
        open={pwOpen}
        title="修改口令"
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        onCancel={() => setPwOpen(false)}
        onOk={() => void onChangePassword()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark={false} preserve={false}>
          <Form.Item
            name="old_password"
            label="当前口令"
            rules={[{ required: true, message: '请输入当前口令' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="新口令"
            rules={[
              { required: true, message: '请输入新口令' },
              { min: 8, message: '至少 8 位' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="确认新口令"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请再输入一次' },
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  !value || getFieldValue('new_password') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error('两次输入的口令不一致')),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
